import { CompanySwitch } from "./CompanySwitch";
import { Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChevronRight,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
} from "lucide-react";
import { useBreadcrumbTrail, useRunListCrumb } from "./breadcrumb";

interface TopbarProps {
  onOpenSearch: () => void;
  onToggleCollapse: () => void;
  collapsed: boolean;
  onOpenMobileNav: () => void;
}

export function Topbar({
  onOpenSearch,
  onToggleCollapse,
  collapsed,
  onOpenMobileNav,
}: TopbarProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const crumbs = useBreadcrumbTrail();
  const runListCrumb = useRunListCrumb();

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b border-line bg-surface/80 px-3 backdrop-blur-sm">
      <button
        type="button"
        onClick={onOpenMobileNav}
        className="grid size-9 place-items-center rounded-sm text-ink-soft hover:bg-sunken lg:hidden"
        aria-label={t("common.openNavigation")}
      >
        <Menu className="size-5" />
      </button>
      <button
        type="button"
        onClick={onToggleCollapse}
        className="hidden size-9 place-items-center rounded-sm text-ink-faint hover:bg-sunken hover:text-ink-soft lg:grid"
        aria-label={t("common.toggleSidebar")}
      >
        {collapsed ? <PanelLeftOpen className="size-[18px]" /> : <PanelLeftClose className="size-[18px]" />}
      </button>

      <nav className="flex min-w-0 items-center gap-1.5 text-sm" aria-label={t("common.breadcrumb")}>
        {crumbs.map((crumb, i) => {
          const last = i === crumbs.length - 1;
          return (
            <Fragment key={`${crumb.to ?? ""}:${crumb.label}:${i}`}>
              {i > 0 ? <ChevronRight className="size-3.5 shrink-0 text-ink-faint" /> : null}
              {last || !crumb.to ? (
                <span
                  className={last ? "truncate font-medium text-ink" : "truncate text-ink-faint"}
                  aria-current={last ? "page" : undefined}
                >
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.to}
                  className="truncate text-ink-faint hover:text-ink"
                  onClick={(e) => {
                    if (pathname === crumb.to) {
                      e.preventDefault();
                      runListCrumb();
                    }
                  }}
                >
                  {crumb.label}
                </Link>
              )}
            </Fragment>
          );
        })}
      </nav>

      <div className="flex-1" />

      <CompanySwitch />

      <button
        type="button"
        onClick={onOpenSearch}
        className="group flex h-9 items-center gap-2 rounded-sm border border-line bg-surface px-2.5 text-sm text-ink-faint hover:border-line-strong"
      >
        <Search className="size-4" />
        <span className="hidden sm:inline">{t("common.search")}</span>
        <kbd className="ml-1 hidden rounded-xs border border-line bg-sunken px-1.5 py-0.5 text-2xs sm:inline">
          ⌘K
        </kbd>
      </button>
    </header>
  );
}
